/* eslint-disable no-console */

import {
  defaultLogLevel,
  LogLevelToRank,
  LogMethodName,
  LogMethodNameToImpl,
  LogMethodToLogLevel,
} from '../types/logging';
import { isDeployPreview, isDevOrTest, isMobile, isNewRelicEnabled, logLevelPreference } from './environment';
import { log as newRelicLog } from './newRelicClient.platform';

export type LogExtra = { [name: string]: unknown; };
export type LogFn = (message: string, extra?: LogExtra) => void;
type ShouldLogFn = (
  methodName: LogMethodName,
  module: string,
  message: Parameters<LogFn>[0],
  extra: Parameters<LogFn>[1],
) => boolean;

export type LoggerOptions = {
  shouldLog?: ShouldLogFn | boolean;
  shouldPrintLogsInProduction?: boolean;
  shouldLogToNewRelic?: boolean;
};

function safeJsonStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    const out = JSON.stringify(value, (_key, val: unknown) => {
      if (typeof val === 'symbol') { return val.toString(); }
      if (typeof val === 'bigint') { return `${val.toString()}n`; }
      if (typeof val === 'function') { return `[Function ${val.name || 'anonymous'}]`; }
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) { return '[Circular]'; }
        seen.add(val);
      }
      return val;
    });
    return out ?? '[undefined]';
  } catch {
    return '[unstringifiable]';
  }
}

// Log methods whose output is forwarded to New Relic when
// `shouldLogToNewRelic` is set. Matches the pre-consolidation set (Time /
// TimeEnd are deliberately not forwarded).
const NEW_RELIC_FORWARDED_METHOD_NAMES: ReadonlyArray<LogMethodName> = [
  LogMethodName.Info,
  LogMethodName.Debug,
  LogMethodName.Error,
  LogMethodName.Warn,
  LogMethodName.Log,
];

// Cap on the stringified `extra` payload for log methods that surface
// through RN's LogBox (`error` and `warn`) — last-resort safety net so
// the red/yellow banner title stays a reasonable length. The bigger
// fix for LogBox is dropping stack traces from Error values for these
// methods (see `_log`); embedded "\n    at ..." substrings make LogBox
// parse the body as a stack and render nothing. Other methods (`info`,
// `debug`, etc.) don't trigger LogBox, so they pass the full payload
// through unchanged.
const MAX_CONSOLE_EXTRA_LEN = 500;

const logLevelToRank = (logLevel: string | undefined): number =>
  (LogLevelToRank as { [key: string]: number; })[logLevel ?? defaultLogLevel] ?? LogLevelToRank[defaultLogLevel];

export class ReadwiseLogger {
  filenameOrName: string;
  module: string;
  shouldLog?: ShouldLogFn;
  shouldPrintLogsInProduction: boolean;
  shouldLogToNewRelic: boolean;

  // The public log methods are defined as arrow-function fields (not
  // prototype methods) so they stay bound to the instance when passed as
  // callbacks (e.g. `somePromise().catch(logger.error)`). As plain methods
  // they'd be invoked as free functions with `this === undefined`, breaking
  // the `this._log` dispatch.
  [LogMethodName.Debug] = (...args: Parameters<LogFn>) => {
    this._log(LogMethodName.Debug, ...args);
  };

  [LogMethodName.Info] = (...args: Parameters<LogFn>) => {
    this._log(LogMethodName.Info, ...args);
  };

  [LogMethodName.Error] = (...args: Parameters<LogFn>) => {
    this._log(LogMethodName.Error, ...args);
  };

  [LogMethodName.Log] = (...args: Parameters<typeof console.log>) => {
    if (this.shouldLog) {
      if (!this.shouldLog(LogMethodName.Log, this.module, '', undefined)) {
        return;
      }
    } else {
      const logLevel = LogMethodToLogLevel[LogMethodName.Log];
      if (logLevelToRank(logLevel) < logLevelToRank(logLevelPreference)) {
        return;
      }
    }
    console.log(...args);
  };

  [LogMethodName.Time] = (message: string) => {
    this._log(LogMethodName.Time, message);
  };

  [LogMethodName.TimeEnd] = (message: string) => {
    this._log(LogMethodName.TimeEnd, message);
  };

  [LogMethodName.Warn] = (...args: Parameters<LogFn>) => {
    this._log(LogMethodName.Warn, ...args);
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(filenameOrName: string, options: LoggerOptions = {}) {
    this.filenameOrName = filenameOrName;
    this.module = this._parseModuleFromFilename(filenameOrName);
    const { shouldLog } = options;
    if (typeof shouldLog === 'boolean') {
      this.shouldLog = () => Boolean(shouldLog);
    } else {
      this.shouldLog = shouldLog;
    }
    this.shouldPrintLogsInProduction = options.shouldPrintLogsInProduction ?? false;
    this.shouldLogToNewRelic = options.shouldLogToNewRelic ?? false;
  }

  get prefix() {
    return `[${this.module}]`;
  }

  protected _parseModuleFromFilename(filenameOrName: string): string {
    let module = filenameOrName;

    // Ends with */*.*
    if (/.+\/.+.+\..+$/.test(module)) {
      module = module.split('/').pop() || '';
      if (['index.', 'methods.', 'models.'].some((prefix) => module.startsWith(prefix))) {
        // "index.ts" is not a very descriptive module name
        module = filenameOrName.split('/').slice(-2).join('/'); // .. so we make it e.g. background/index.ts
      }
      if (module.includes('.')) {
        module = module.split('.').slice(0, -1).join('.'); // directory/module.ts -> directory/module
      }
    }

    return module;
  }

  protected _log(
    methodName: LogMethodName,
    message: string,
    extra?: LogExtra,
  ): void {
    // Logging must never break callers. Wrap the whole body so any failure
    // here (New Relic, serialization, console polyfill, etc.) is swallowed
    // instead of escaping into application control flow.
    try {
      if (this.shouldLogToNewRelic && NEW_RELIC_FORWARDED_METHOD_NAMES.includes(methodName) && isNewRelicEnabled && !isDevOrTest) {
        // Preserving pre-consolidation behavior: every forwarded methodName
        // lands in New Relic at INFO level. Mapping Warn/Error/etc. to their
        // natural New Relic levels is a follow-up — doing it here could
        // change alerting thresholds for any existing saved queries.
        newRelicLog.info(message, extra);
      }
      if (!this._shouldLog(methodName, message, extra)) {
        return;
      }

      const logImpl = LogMethodNameToImpl[methodName];

      if (extra) {
        if (isMobile) {
          try {
            // `console.error` / `console.warn` go through RN's LogBox, whose
            // parser interprets "\n    at ..." substrings as a stack trace
            // and renders the banner body empty when it finds them in the
            // message. For those two methods, flatten Error values to
            // `.message` only (no stack). For everything else, keep the
            // stack — `info` / `debug` / etc. don't trigger LogBox and the
            // stack is what makes them useful in Metro. Either way the raw
            // `extra` (with the full Error object) was already forwarded to
            // NewRelic above, so production debugging is unaffected.
            const isLogBoxMethod = methodName === LogMethodName.Error || methodName === LogMethodName.Warn;
            for (const key of Object.keys(extra)) {
              const value = extra[key];
              if (value instanceof Error) {
                extra[key] = isLogBoxMethod ? value.message : (value.stack ?? value.message);
              } else if (value instanceof Array && safeJsonStringify(value).length >= 1000) {
                extra[key] = value.length;
              }
            }
            const safeExtra = Object.entries(extra)
              .map(([key, value]) => `${key}=${safeJsonStringify(value)}`)
              .join(', ');
            // Safety net: cap LogBox-emitting messages at a reasonable
            // length even after stack-stripping, in case some other field
            // carries a huge payload.
            const consoleExtra = isLogBoxMethod && safeExtra.length > MAX_CONSOLE_EXTRA_LEN
              ? `${safeExtra.slice(0, MAX_CONSOLE_EXTRA_LEN - 3)}...`
              : safeExtra;
            logImpl(`${this.prefix} ${message}: ${consoleExtra}`);
          } catch {
            // Never pass `extra` to the dev console as a fallback: RN's util.inspect
            // can throw on values that safeJsonStringify also couldn't handle (eg.
            // RxDB instances with internal Symbols under newer Hermes), and that
            // throw escapes the logger and breaks callers' control flow.
            logImpl(`${this.prefix} ${message} [extra omitted: failed to stringify]`);
          }
        } else {
          logImpl(`${this.prefix} ${message}`, extra);
        }
      } else {
        logImpl(`${this.prefix} ${message}`);
      }
    } catch {
      // Swallow: a failing log must not propagate. Intentionally no re-log
      // attempt here to avoid recursion on a broken console.
    }
  }

  protected _shouldLog(methodName: LogMethodName, message: string, extra: LogExtra | undefined): boolean {
    if (!this.shouldPrintLogsInProduction && !isDevOrTest && !isDeployPreview) {
      return false;
    }
    if (this.shouldLog) {
      if (!this.shouldLog(methodName, this.module, message, extra)) {
        return false;
      }
    } else {
      const logLevel = LogMethodToLogLevel[methodName];
      if (logLevelToRank(logLevel) < logLevelToRank(logLevelPreference)) {
        return false;
      }
    }
    return true;
  }
}

/**
 * Create a logger.
 *
 * @param filenameOrName Name that is prefixed to every log message. Pass `__filename` unless you want a custom name like `'database'`.
 * @param options
 */
export default function makeLogger(filenameOrName: string, options: LoggerOptions = {}): ReadwiseLogger {
  return new ReadwiseLogger(filenameOrName, options);
}
